import {
  BookingMode,
  ExperienceImageSizeMediaSchema,
  ListingKind,
} from '@kouto/types'
import { z } from 'zod'

export enum DiscountType {
  COUPON = 'coupon',
  CREDIT_CODE = 'credit_code',
}

export enum TaxType {
  PERCENTAGE = 'percentage',
  FLAT = 'flat',
}

export type CreditCode = {
  code: string
  discountType: DiscountType
  id: string
  type: string
  value: number
}

type ResourceGroup = {
  taxes?: Tax[]
  id: string
}

type Tax = {
  includedInPrice?: boolean
  name: string
} & (
  | {
      percentage: number
    }
  | { amount: number }
)

export type DiscountCode = {
  discountCode: {
    code: string
    type: string
    value: number
    experienceIds: string[]
    blackoutExperienceIds?: string[]
    discountType?: DiscountType
  }
}

export type Listing = {
  taxes: Tax[] | null
  defaultMaxParticipantCount?: number | null
  resourceGroups?: ResourceGroup[] | null
  kind: ListingKind
  map?: z.infer<typeof ExperienceImageSizeMediaSchema> | null
}

export type Tier = {
  name: string
  price: number
  selectedNumber: number
}

export type IncludedTax = {
  name: string
  percentage: number
}

export type NonIncludedTax = {
  name: string
  amountFee: number
  type?: string
}

export type CartItemTotalDetails = {
  isPrivateBooking: boolean
  selectedPriceTiers: Tier[]
  percentageDiscountByCode: number
  fixedPercentageDiscount: number
  fixedAmountDiscount: number
  fixedExperienceAmountDiscount: number
  totalPriceTiersAmount: number
  totalAddOnsAmount: number
  priceTiersDiscountAmount: number
  addOnsDiscountAmount: number
  totalDiscountAmount: number
  priceTiersTaxes: NonNullable<Listing['taxes']>
  includedPriceTiersTaxes: IncludedTax[]
  nonIncludedPriceTiersTaxes: NonIncludedTax[]
  includedAddOnsTaxes: IncludedTax[]
  nonIncludedAddOnsTaxes: NonIncludedTax[]
  includedTaxes: IncludedTax[]
  nonIncludedTaxes: NonIncludedTax[]
  subTotal: number
  total: number
}

export type CartTotalDetails = {
  cartSubTotal: number
  cartTotal: number
  totalDiscountAmount: number
  creditCodesTotalDiscountAmount: number
  includedTaxes: IncludedTax[]
  nonIncludedTaxes: NonIncludedTax[]
  cartItemsDetails: Record<string, CartItemTotalDetails>
}

export const emptyCartTotalDetails = {
  cartSubTotal: 0,
  cartTotal: 0,
  totalDiscountAmount: 0,
  creditCodesTotalDiscountAmount: 0,
  includedTaxes: [],
  nonIncludedTaxes: [],
  cartItemsDetails: {},
}

type MinimalParticipant = {
  priceTierName: string
  price: number
}

type MinimalAddOn = {
  price: number
  selectedNumber: number
  taxes?: Listing['taxes']
}

export type MinimalCartItem = {
  cartItemId: string
  listingId?: string
  experienceId: string
  maxParticipantCountForPrivateBooking?: number
  participants: MinimalParticipant[]
  groupId?: string // if the experience is part of a resourceGroup, we need to get the taxes from the group
  addOns?: MinimalAddOn[]
  bookingMode: BookingMode
}

const getCreditCodeValue = (item: CreditCode): number => {
  if ('amount' in item && typeof item.amount === 'string') {
    const numericAmount = parseFloat(item.amount)
    return isNaN(numericAmount) ? 0 : numericAmount
  }

  if ('value' in item && typeof item.value === 'number') {
    return item.value
  }

  return 0
}

const getItemSelectedPriceTiers = (
  cartItem: MinimalCartItem,
  listing: Listing,
  isPrivateBooking: boolean,
): Tier[] =>
  (isPrivateBooking
    ? [
        {
          priceTierName: 'private',
          price:
            (cartItem?.maxParticipantCountForPrivateBooking ??
              listing.defaultMaxParticipantCount ??
              1) * cartItem.participants[0].price,
          selectedNumber: 1,
        },
      ]
    : cartItem.participants
  )
    .map((participant, _, arr) => ({
      name: participant.priceTierName,
      price: participant.price,
      selectedNumber: arr.filter(
        p =>
          p.priceTierName === participant.priceTierName &&
          p.price === participant.price,
      ).length,
    }))
    .filter(
      (e, i, a) =>
        a.findIndex(p => p.name === e.name && p.price === e.price) === i,
    )

const getItemPriceTiersDiscountAmount = (
  totalPriceTiersAmount: number,
  percentageDiscount = 0, // 0 to 100
  fixedAmountDiscount = 0,
) => {
  const discountByPercentage =
    (totalPriceTiersAmount * percentageDiscount) / 100
  const totalDiscount = discountByPercentage + fixedAmountDiscount
  return Math.min(round(totalDiscount), totalPriceTiersAmount)
}

const getItemAddOnsDiscountAmount = (
  totalAddOnsAmount: number,
  percentageDiscount = 0, // 0 to 100
  fixedAmountDiscount = 0,
) => {
  const discountByPercentage = (totalAddOnsAmount * percentageDiscount) / 100
  const totalDiscount = discountByPercentage + fixedAmountDiscount
  return Math.min(round(totalDiscount), totalAddOnsAmount)
}

const getItemPriceTiersTaxes = (
  cartItem: MinimalCartItem,
  listing: Listing,
): NonNullable<Listing['taxes']> => {
  if (cartItem.groupId && listing.resourceGroups) {
    const resourceGroup = listing.resourceGroups.find(
      group => group.id === cartItem.groupId,
    )
    return resourceGroup?.taxes || []
  }
  return listing.taxes || []
}

const getItemIncludedPriceTiersTaxes = (
  itemPriceTiersTaxes: NonNullable<Listing['taxes']>,
  itemTotalPriceTiersAmount: number,
): IncludedTax[] =>
  itemTotalPriceTiersAmount > 0
    ? (itemPriceTiersTaxes?.filter(tax => tax.includedInPrice) ?? []).map(
        tax => ({
          name: tax.name,
          percentage: 'percentage' in tax ? tax.percentage : 0,
        }),
      )
    : []

const getIncludedPriceTaxesOnResourceExperiences = (
  itemPriceTiersTaxes: NonNullable<Listing['taxes']>,
  itemTotalPriceTiersAmount: number,
  priceTiersCount: number,
  ifResourceExperience: boolean,
) => {
  if (!ifResourceExperience) {
    return []
  }

  if (itemTotalPriceTiersAmount <= 0) {
    return []
  }

  const result = (itemPriceTiersTaxes.filter(tax => !tax.includedInPrice) ?? [])
    .map(tax => {
      const baseAmount = itemTotalPriceTiersAmount / priceTiersCount

      if ('percentage' in tax && typeof tax.percentage === 'number') {
        return {
          name: tax.name,
          amountFee: round(baseAmount * (tax.percentage / 100)),
        }
      }

      if ('amount' in tax && typeof tax.amount === 'number') {
        return {
          name: tax.name,
          amountFee: round(tax.amount),
        }
      }

      return []
    })
    .filter(Boolean)
    .flat()

  return result
}

const getTotalForResourceExperience = (
  total: number,
  count: number,
  taxes: { name: string; amountFee: number }[],
  nonIncludedAddOnsTaxes: { name: string; amountFee: number }[],
): number | null => {
  if (taxes && taxes.length === 0) return null
  const baseAmount = total / count
  const totalWithTaxes = Array.from({ length: count }).reduce((acc: number) => {
    const totalTaxForParticipant = taxes.reduce(
      (taxAcc, tax) => taxAcc + tax.amountFee,
      0,
    )
    const amountWithTaxes =
      round((baseAmount + totalTaxForParticipant) * 100) / 100
    return acc + amountWithTaxes
  }, 0)
  const totalAddOnsTaxes = nonIncludedAddOnsTaxes.reduce(
    (acc, tax) => acc + tax.amountFee,
    0,
  )
  return totalWithTaxes + totalAddOnsTaxes
}

export const getItemNonIncludedPriceTiersTaxes = (
  itemPriceTiersTaxes: NonNullable<Listing['taxes']>,
  itemTotalPriceTiersAmount: number,
  priceTiersCount: number,
  applyFlatFeeToEachTier = false,
  applyFlatFeeTaxes = true,
) =>
  /* eslint-disable no-nested-ternary */
  itemTotalPriceTiersAmount > 0
    ? (itemPriceTiersTaxes.filter(tax => !tax.includedInPrice) ?? []).map(
        tax => {
          let amountFee = 0
          if ('percentage' in tax) {
            amountFee = itemTotalPriceTiersAmount * (tax.percentage / 100)
          } else if (applyFlatFeeTaxes) {
            amountFee =
              tax.amount * (applyFlatFeeToEachTier ? priceTiersCount : 1)
          }

          return {
            name: tax.name,
            amountFee,
            type: 'percentage' in tax ? TaxType.PERCENTAGE : TaxType.FLAT,
          }
        },
      )
    : []
/* eslint-enable no-nested-ternary */

const getItemIncludedAddOnsTaxes = (addOns: MinimalAddOn[]) =>
  (
    addOns
      .filter(addOn => addOn.price > 0) // if addOn is free ==> event the flatFee taxes are ignored
      .flatMap(addOn =>
        (addOn.taxes?.filter(tax => tax.includedInPrice) ?? []).map(tax => ({
          name: tax.name,
          percentage: 'percentage' in tax ? tax.percentage : 0,
        })),
      ) ?? []
  ).filter((e, i, a) => a.findIndex(tax => tax.name === e.name) === i)

export const getItemNonIncludedAddOnsTaxes = (
  addOns: MinimalAddOn[],
  totalAddOnsAmount: number,
  fixedAddOnsDiscountAmount: number,
  applyFlatFeeToEachAddOn = true, // here we are deciding that the flatFee tax is due for each addOn selected
) =>
  addOns
    .filter(addOn => addOn.price > 0) // if addOn is free ==> even the flatFee taxes are ignored
    .reduce(
      (acc, addOn) => {
        const addOnTotalPrice = addOn.price * addOn.selectedNumber
        const addOnFixedDiscountPortion =
          (fixedAddOnsDiscountAmount * addOnTotalPrice) / totalAddOnsAmount
        const addOnTotalPriceIncludingFixedDiscount = round(
          addOnTotalPrice - addOnFixedDiscountPortion,
        )

        const nonIncludedAddonTaxes = (
          addOn.taxes?.filter(tax => !tax.includedInPrice) ?? []
        ).map(tax => ({
          name: tax.name,
          /* eslint-disable no-nested-ternary */
          amountFee: round(
            'percentage' in tax
              ? addOnTotalPriceIncludingFixedDiscount * (tax.percentage / 100)
              : applyFlatFeeToEachAddOn
              ? tax.amount * addOn.selectedNumber
              : tax.amount,
          ),
          /* eslint-enable no-nested-ternary */
        }))
        nonIncludedAddonTaxes.forEach(tax => {
          const existingTax = acc.find(item => item.name === tax.name)
          if (existingTax) {
            existingTax.amountFee += tax.amountFee
          } else {
            acc.push(tax)
          }
        })

        return acc
      },
      [] as {
        name: string
        amountFee: number
      }[],
    ) || []

const getAllItemIncludedTaxes = (
  includedPriceTiersTaxes: IncludedTax[],
  includedAddOnsTaxes: IncludedTax[],
): IncludedTax[] =>
  structuredClone(includedPriceTiersTaxes)
    .concat(structuredClone(includedAddOnsTaxes))
    .filter(
      (e, i, a) =>
        a.findIndex(
          tax => tax.name === e.name && tax.percentage === e.percentage,
        ) === i,
    )

export const getAllItemNonIncludedTaxes = (
  nonIncludedPriceTiersTaxes: NonIncludedTax[],
  nonIncludedAddOnsTaxes: NonIncludedTax[],
): NonIncludedTax[] => {
  const taxes = structuredClone(nonIncludedPriceTiersTaxes)
  nonIncludedAddOnsTaxes.forEach(tax => {
    const existingTax = taxes.find(item => item.name === tax.name)
    if (existingTax) {
      existingTax.amountFee += tax.amountFee
    } else {
      taxes.push(tax)
    }
  })

  return taxes
}

const updateCartItem = (
  cartItem: MinimalCartItem,
  isRequestToBookEnabled: boolean,
) => {
  const updatedParticipants = isRequestToBookEnabled
    ? cartItem.participants.slice(0, -1)
    : cartItem.participants

  return {
    ...cartItem,
    participants: updatedParticipants,
  }
}

export const computeOneCartItemTotalDetails = (
  cartItem: MinimalCartItem,
  listing: Listing,
  percentageDiscountByCode = 0, // applies just to experiences
  fixedPercentageDiscount = 0, // applies to all experiences and addOns
  fixedAmountDiscount = 0, // this cart item portion of the original fixedAmountDiscount, to split proportionally between all its priceTiers and addOns
  isRequestToBookEnabled = false,
  fixedExperienceAmountDiscount = 0, // amount coupon portion applied only to experience price tiers
): CartItemTotalDetails => {
  const isPrivateBooking =
    cartItem.bookingMode === BookingMode.PRIVATE &&
    listing.kind === ListingKind.EXPERIENCE

  const updatedCartItem = updateCartItem(cartItem, isRequestToBookEnabled)

  const selectedPriceTiers = getItemSelectedPriceTiers(
    updatedCartItem,
    listing,
    isPrivateBooking,
  )

  const totalPriceTiersAmountBeforeDiscount = selectedPriceTiers.reduce(
    (acc, { price, selectedNumber }) => acc + price * selectedNumber,
    0,
  )

  const totalAddOnsAmountBeforeDiscount =
    cartItem.addOns?.reduce(
      (acc, addOn) => acc + addOn.selectedNumber * addOn.price,
      0,
    ) ?? 0

  const itemSubtotalBeforeDiscounts =
    totalPriceTiersAmountBeforeDiscount + totalAddOnsAmountBeforeDiscount

  const fixedPriceTiersDiscountAmount =
    itemSubtotalBeforeDiscounts > 0
      ? round(
          (fixedAmountDiscount * totalPriceTiersAmountBeforeDiscount) /
            itemSubtotalBeforeDiscounts,
        )
      : 0

  const priceTiersDiscountAmount = getItemPriceTiersDiscountAmount(
    totalPriceTiersAmountBeforeDiscount,
    Math.min(100, percentageDiscountByCode + fixedPercentageDiscount),
    fixedPriceTiersDiscountAmount + fixedExperienceAmountDiscount,
  )
  const totalPriceTiersAmount = Math.max(
    0,
    round(totalPriceTiersAmountBeforeDiscount - priceTiersDiscountAmount),
  )

  const fixedAddOnsDiscountAmount = round(
    fixedAmountDiscount - fixedPriceTiersDiscountAmount,
  )

  const addOnsDiscountAmount = getItemAddOnsDiscountAmount(
    totalAddOnsAmountBeforeDiscount,
    fixedPercentageDiscount,
    fixedAddOnsDiscountAmount,
  )
  const totalAddOnsAmount = Math.max(
    0,
    round(totalAddOnsAmountBeforeDiscount - addOnsDiscountAmount),
  )
  const skipAddOnsTaxes = totalAddOnsAmount === 0 // if addOns become free thanks to a discount ==> event flatFee taxes are ignored

  const totalDiscountAmount = priceTiersDiscountAmount + addOnsDiscountAmount

  const priceTiersTaxes = getItemPriceTiersTaxes(cartItem, listing)

  const includedPriceTiersTaxes = getItemIncludedPriceTiersTaxes(
    priceTiersTaxes,
    totalPriceTiersAmount,
  )

  const nonIncludedPriceTiersTaxes = getItemNonIncludedPriceTiersTaxes(
    priceTiersTaxes,
    totalPriceTiersAmount,
    cartItem.participants.length,
    listing.kind === ListingKind.RESOURCE && !listing.map,
  )

  const includedPriceTaxesOnResourceExperiences =
    getIncludedPriceTaxesOnResourceExperiences(
      priceTiersTaxes,
      totalPriceTiersAmount,
      cartItem.participants.length,
      listing.kind === ListingKind.RESOURCE,
    )

  const includedAddOnsTaxes = skipAddOnsTaxes
    ? []
    : getItemIncludedAddOnsTaxes(cartItem.addOns || [])
  const nonIncludedAddOnsTaxes = skipAddOnsTaxes
    ? []
    : getItemNonIncludedAddOnsTaxes(
        cartItem.addOns || [],
        totalAddOnsAmountBeforeDiscount,
        addOnsDiscountAmount,
      )

  const includedTaxes = getAllItemIncludedTaxes(
    includedPriceTiersTaxes,
    includedAddOnsTaxes,
  )

  const nonIncludedTaxes = getAllItemNonIncludedTaxes(
    nonIncludedPriceTiersTaxes,
    nonIncludedAddOnsTaxes,
  )

  const itemSubTotal = round(totalPriceTiersAmount + totalAddOnsAmount)

  let itemTotal
  const totalForResourceExperience = getTotalForResourceExperience(
    itemSubTotal,
    cartItem.participants.length,
    includedPriceTaxesOnResourceExperiences,
    nonIncludedAddOnsTaxes,
  )
  if (totalForResourceExperience) {
    itemTotal = totalForResourceExperience
  } else {
    itemTotal = round(
      itemSubTotal +
        nonIncludedTaxes.reduce((acc, tax) => acc + tax.amountFee, 0),
    )
  }

  return {
    isPrivateBooking,
    selectedPriceTiers,
    percentageDiscountByCode,
    fixedPercentageDiscount,
    fixedAmountDiscount,
    fixedExperienceAmountDiscount,
    totalPriceTiersAmount,
    totalAddOnsAmount,
    priceTiersDiscountAmount,
    addOnsDiscountAmount,
    totalDiscountAmount,
    priceTiersTaxes,
    includedPriceTiersTaxes,
    nonIncludedPriceTiersTaxes,
    includedAddOnsTaxes,
    nonIncludedAddOnsTaxes,
    includedTaxes,
    nonIncludedTaxes,
    subTotal: itemSubTotal,
    total: itemTotal,
  }
}

const computeAllCartItemsTotalDetails = (
  cartItems: Record<string, MinimalCartItem>,
  listings: Record<string, Listing>,
  discountCode?: null | DiscountCode['discountCode'], // applies just to experiences
  fixedPercentageDiscount = 0, // applies to all experiences and addOns
  fixedAmountDiscount = 0, // to split proportionally between cartItems
) => {
  const discountCodeExperienceIds = discountCode?.experienceIds || []

  let cartItemsDetails = Object.values(cartItems).reduce((acc, cartItem) => {
    const listing = listings[cartItem.listingId || cartItem.experienceId]
    if (listing) {
      const percentageDiscountByCode =
        discountCode?.type === 'percentage' &&
        discountCodeExperienceIds.includes(cartItem.experienceId)
          ? discountCode.value
          : 0

      acc[cartItem.cartItemId] = computeOneCartItemTotalDetails(
        cartItem,
        listing,
        percentageDiscountByCode,
        fixedPercentageDiscount,
      )
    }
    return acc
  }, {} as Record<string, CartItemTotalDetails>)

  if (discountCode?.type === 'amount') {
    const eligibleCartItemIds = Object.keys(cartItemsDetails).filter(
      cartItemId =>
        discountCodeExperienceIds.includes(cartItems[cartItemId].experienceId),
    )
    const eligibleSubtotal = eligibleCartItemIds.reduce(
      (total, cartItemId) =>
        total + cartItemsDetails[cartItemId].totalPriceTiersAmount,
      0,
    )
    const effectiveDiscount = Math.min(discountCode.value, eligibleSubtotal)

    // Coupon codes exclude add-ons, so distribute the amount only across eligible experience prices.
    if (effectiveDiscount > 0 && eligibleSubtotal > 0) {
      cartItemsDetails = Object.keys(cartItemsDetails).reduce(
        (acc, cartItemId) => {
          const cartItem = cartItems[cartItemId]
          const listing =
            listings[cartItem?.listingId || cartItem?.experienceId]
          const previousDetails = cartItemsDetails[cartItemId]
          if (cartItem && listing && previousDetails) {
            const itemDiscount = eligibleCartItemIds.includes(cartItemId)
              ? round(
                  (effectiveDiscount * previousDetails.totalPriceTiersAmount) /
                    eligibleSubtotal,
                )
              : 0
            acc[cartItemId] = computeOneCartItemTotalDetails(
              cartItem,
              listing,
              0,
              previousDetails.fixedPercentageDiscount,
              0,
              false,
              itemDiscount,
            )
          }
          return acc
        },
        {} as Record<string, CartItemTotalDetails>,
      )
    }
  }

  // To apply the fixedAmountDiscount proportionally between all cartItems, we need to know the cartSubTotal.
  // To know the cartSubTotal, we need to compute the subTotal of each cartItem without fixedAmountDiscount.
  // So if we have fixedAmountDiscount, we need to compute all items totalDetails a second time,
  //    this time passing to each item their portion of the original fixedAmountDiscount

  if (fixedAmountDiscount) {
    const cartSubTotal = Object.values(cartItemsDetails).reduce(
      (acc, item) => acc + item.subTotal,
      0,
    )

    cartItemsDetails = Object.keys(cartItemsDetails).reduce(
      (acc, cartItemId) => {
        const cartItem = cartItems[cartItemId]
        const listing = listings[cartItem?.listingId || cartItem?.experienceId]
        const previousCartItemTotalDetails = cartItemsDetails[cartItemId]
        if (cartItem && listing && previousCartItemTotalDetails) {
          const itemFixedAmountDiscountPortion = round(
            (fixedAmountDiscount * previousCartItemTotalDetails.subTotal) /
              cartSubTotal,
          )

          acc[cartItemId] = computeOneCartItemTotalDetails(
            cartItem,
            listing,
            previousCartItemTotalDetails.percentageDiscountByCode,
            previousCartItemTotalDetails.fixedPercentageDiscount,
            itemFixedAmountDiscountPortion,
            false,
            previousCartItemTotalDetails.fixedExperienceAmountDiscount,
          )
        }

        return acc
      },
      {} as Record<string, CartItemTotalDetails>,
    )
  }

  return cartItemsDetails
}

export const computeCartTotalDetails = (
  cartItems: Record<string, MinimalCartItem>,
  listings: Record<string, Listing>,
  options?: {
    discountCode?: null | DiscountCode['discountCode'] // applies just to experiences
    creditCodes?: null | CreditCode[] // applies total
    fixedPercentageDiscount?: number // applies to all experiences and addOns
    fixedAmountDiscount?: number // to split proportionally between cartItems
  },
): CartTotalDetails => {
  const cartItemsDetails = computeAllCartItemsTotalDetails(
    cartItems,
    listings,
    options?.discountCode,
    options?.fixedPercentageDiscount,
    options?.fixedAmountDiscount,
  )

  const cartSubTotal = Object.values(cartItemsDetails).reduce(
    (acc, item) => acc + item.subTotal,
    0,
  )

  const cartTotal = Object.values(cartItemsDetails).reduce(
    (acc, item) => acc + item.total,
    0,
  )
  const totalDiscountAmount = Object.values(cartItemsDetails).reduce(
    (acc, item) => acc + item.totalDiscountAmount,
    0,
  )

  const includedTaxes = Object.values(cartItemsDetails).reduce(
    (acc, item) =>
      acc
        .concat(item.includedTaxes)
        .filter(
          (e, i, a) =>
            a.findIndex(
              tax => tax.name === e.name && tax.percentage === e.percentage,
            ) === i,
        ),
    [] as IncludedTax[],
  )

  const nonIncludedTaxes = Object.values(cartItemsDetails).reduce(
    (acc, item) => {
      item.nonIncludedTaxes.forEach(tax => {
        const existingTax = acc.find(t => t.name === tax.name)
        if (existingTax) {
          existingTax.amountFee += tax.amountFee
        } else {
          acc.push(tax)
        }
      })
      return acc
    },
    [] as NonIncludedTax[],
  )

  const creditCodesTotalDiscountAmount = Math.min(
    cartTotal, // credits can't make the cart total negative
    Object.values(options?.creditCodes || []).reduce((acc, item) => {
      return acc + getCreditCodeValue(item)
    }, 0),
  )

  return {
    cartSubTotal,
    cartTotal: round(cartTotal - creditCodesTotalDiscountAmount),
    totalDiscountAmount: round(
      totalDiscountAmount + creditCodesTotalDiscountAmount,
    ),
    creditCodesTotalDiscountAmount: round(creditCodesTotalDiscountAmount),
    includedTaxes,
    nonIncludedTaxes,
    cartItemsDetails,
  }
}

export const round = (number: number, decimals = 2): number => {
  const factor = 10 ** decimals
  return Math.round(number * factor) / factor
}

// Test-only exposure of internal helpers to facilitate granular unit testing.
// Does not affect runtime behavior.
// eslint-disable-next-line @typescript-eslint/naming-convention
export const __test__ = {
  getCreditCodeValue,
  getItemSelectedPriceTiers,
  getItemPriceTiersDiscountAmount,
  getItemAddOnsDiscountAmount,
  computeOneCartItemTotalDetails,
  // expose internal aggregator for focused testing
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  computeAllCartItemsTotalDetails,
  getItemPriceTiersTaxes,
  getItemIncludedPriceTiersTaxes,
  getIncludedPriceTaxesOnResourceExperiences,
  getTotalForResourceExperience,
  getItemNonIncludedPriceTiersTaxes,
  getItemIncludedAddOnsTaxes,
  getItemNonIncludedAddOnsTaxes,
  getAllItemIncludedTaxes,
  getAllItemNonIncludedTaxes,
  updateCartItem,
  round,
}
